import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsersStatistic } from '../../api';
import '../../App.css';

const languageNames = {
    en: { name: 'English', flag: '🇺🇸' },
    jp: { name: 'Japanese', flag: '🇯🇵' },
    ja: { name: 'Japanese', flag: '🇯🇵' },
    es: { name: 'Spanish', flag: '🇪🇸' },
    fr: { name: 'French', flag: '🇫🇷' },
    de: { name: 'German', flag: '🇩🇪' },
    zh: { name: 'Chinese', flag: '🇨🇳' },
    ko: { name: 'Korean', flag: '🇰🇷' },
    it: { name: 'Italian', flag: '🇮🇹' },
    ru: { name: 'Russian', flag: '🇷🇺' },
    pt: { name: 'Portuguese', flag: '🇵🇹' }
};

const getLanguageInfo = (code) => {
    const key = code.toLowerCase();
    return languageNames[key] || { name: code.toUpperCase(), flag: '🌐' };
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getUsersStatistic();
            setStats(data);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error('Error fetching user statistics:', err);
            setError(err.response?.data?.message || 'Failed to fetch user statistics. Please check server connection.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        fetchStats();
    }, [navigate, fetchStats]);

    const token = localStorage.getItem('token');
    if (!token) return null;

    const totalUsers = stats?.totalUsers || 0;
    const activeUsers = stats?.activeUsers || 0;
    const activePct = totalUsers > 0 ? ((activeUsers / totalUsers) * 100).toFixed(1) : 0;
    const strikeAvg = (stats?.totalActiveDaysStrikeAverage || 0).toFixed(2);

    const engagementMetrics = [
        {
            title: 'Passed Tutorial',
            count: stats?.passedTutorialUsers || 0,
            icon: '🎓',
            color: '#3b82f6',
            bgColor: '#eff6ff'
        },
        {
            title: 'Swiped Cards',
            count: stats?.usersSwipedCards || 0,
            icon: '🃏',
            color: '#8b5cf6',
            bgColor: '#f5f3ff'
        },
        {
            title: 'Started Conversation',
            count: stats?.usersStartedConversation || 0,
            icon: '💬',
            color: '#10b981',
            bgColor: '#ecfdf5'
        },
        {
            title: 'Started Course',
            count: stats?.usersStartedCourse || 0,
            icon: '📚',
            color: '#f59e0b',
            bgColor: '#fffbeb'
        }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header & Navigation */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '30px',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '20px'
                }}>
                    <div>
                        <h1 style={{
                            color: '#3c5a5d',
                            margin: '0 0 6px 0',
                            fontSize: '2.5rem',
                            fontWeight: '800'
                        }}>
                            Admin Dashboard
                        </h1>
                        <p style={{ margin: 0, color: '#707F81', fontSize: '1rem', fontWeight: '500' }}>
                            Real-time platform usage analytics and user statistics overview
                        </p>
                    </div>
                </div>

                {/* Sub-header with Refresh & Last Updated */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px'
                }}>
                    <div style={{ color: '#707F81', fontSize: '0.9rem', fontWeight: '500' }}>
                        {lastUpdated && <span>Last updated at <strong>{lastUpdated}</strong></span>}
                    </div>
                    <button
                        onClick={fetchStats}
                        disabled={loading}
                        style={{
                            background: '#3c5a5d',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '10px 18px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: loading ? 0.7 : 1,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#2c4345'; }}
                        onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#3c5a5d'; }}
                    >
                        <span style={{
                            display: 'inline-block',
                            transform: loading ? 'rotate(360deg)' : 'none',
                            transition: 'transform 1s linear'
                        }}>🔄</span>
                        {loading ? 'Refreshing...' : 'Refresh Stats'}
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '24px',
                        padding: '16px 20px',
                        background: '#fee2e2',
                        borderLeft: '4px solid #dc2626',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={fetchStats}
                            style={{
                                background: '#dc2626',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Main Content Skeleton or Cards */}
                {loading && !stats ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 20px',
                        color: '#707F81',
                        fontSize: '1.1rem',
                        fontWeight: '500'
                    }}>
                        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📊</div>
                        Loading user statistics...
                    </div>
                ) : (
                    <>
                        {/* Top Key Metrics Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '20px',
                            marginBottom: '32px'
                        }}>
                            {/* Total Users */}
                            <div
                                onClick={() => navigate('/registration-statistic')}
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    padding: '24px',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                    border: '1px solid #e2e8f0',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.15)';
                                    e.currentTarget.style.borderColor = '#93c5fd';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Total Users
                                    </span>
                                    <span style={{ fontSize: '1.5rem', background: '#eff6ff', padding: '8px', borderRadius: '10px' }}>👥</span>
                                </div>
                                <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0c1415', marginBottom: '6px' }}>
                                    {totalUsers.toLocaleString()}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#707F81', fontSize: '0.85rem' }}>
                                    <span>Registered platform accounts</span>
                                    <span style={{ color: '#3b82f6', fontWeight: '700' }}>View chart →</span>
                                </div>
                            </div>

                            {/* Active Users */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #10b981, #34d399)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Active Users
                                    </span>
                                    <span style={{ fontSize: '1.5rem', background: '#ecfdf5', padding: '8px', borderRadius: '10px' }}>⚡</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0c1415' }}>
                                        {activeUsers.toLocaleString()}
                                    </span>
                                    <span style={{
                                        background: '#d1fae5',
                                        color: '#065f46',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '0.85rem',
                                        fontWeight: '700'
                                    }}>
                                        {activePct}%
                                    </span>
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Active user participation rate
                                </div>
                            </div>

                            {/* New Users Growth */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        New Signups
                                    </span>
                                    <span style={{ fontSize: '1.5rem', background: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>📈</span>
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '8px',
                                    marginBottom: '6px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#0c1415' }}>
                                            {stats?.newUsersToday ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#707F81', fontWeight: '600' }}>Today</div>
                                    </div>
                                    <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#0c1415' }}>
                                            {stats?.newUsersThisWeek ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#707F81', fontWeight: '600' }}>Week</div>
                                    </div>
                                    <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#0c1415' }}>
                                            {stats?.newUsersThisMonth ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#707F81', fontWeight: '600' }}>Month</div>
                                    </div>
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Recent user registration velocity
                                </div>
                            </div>

                            {/* Average Active Days Streak */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Avg. Active Streak
                                    </span>
                                    <span style={{ fontSize: '1.5rem', background: '#fffbeb', padding: '8px', borderRadius: '10px' }}>🔥</span>
                                </div>
                                <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0c1415', marginBottom: '6px' }}>
                                    {strikeAvg} <span style={{ fontSize: '1.1rem', color: '#707F81', fontWeight: '600' }}>days</span>
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Average consecutive active days
                                </div>
                            </div>
                        </div>

                        {/* Engagement & Funnel Breakdown Section */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '28px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                            border: '1px solid #e2e8f0',
                            marginBottom: '32px'
                        }}>
                            <h2 style={{
                                color: '#3c5a5d',
                                fontSize: '1.4rem',
                                fontWeight: '700',
                                margin: '0 0 20px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                🎯 User Engagement & Feature Adoption
                            </h2>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                gap: '20px'
                            }}>
                                {engagementMetrics.map((item, index) => {
                                    const pct = totalUsers > 0 ? ((item.count / totalUsers) * 100).toFixed(1) : 0;
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                background: '#f8fafc',
                                                borderRadius: '14px',
                                                padding: '20px',
                                                border: '1px solid #f1f5f9'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{
                                                        background: item.bgColor,
                                                        padding: '8px',
                                                        borderRadius: '10px',
                                                        fontSize: '1.2rem'
                                                    }}>
                                                        {item.icon}
                                                    </span>
                                                    <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.05rem' }}>
                                                        {item.title}
                                                    </span>
                                                </div>
                                                <span style={{
                                                    fontSize: '0.85rem',
                                                    fontWeight: '700',
                                                    color: item.color,
                                                    background: item.bgColor,
                                                    padding: '3px 8px',
                                                    borderRadius: '8px'
                                                }}>
                                                    {pct}%
                                                </span>
                                            </div>

                                            <div style={{
                                                fontSize: '1.8rem',
                                                fontWeight: '800',
                                                color: '#0f172a',
                                                marginBottom: '10px'
                                            }}>
                                                {item.count.toLocaleString()} <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '500' }}>users</span>
                                            </div>

                                            {/* Progress Bar */}
                                            <div style={{
                                                width: '100%',
                                                height: '8px',
                                                background: '#e2e8f0',
                                                borderRadius: '4px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${Math.min(pct, 100)}%`,
                                                    height: '100%',
                                                    background: item.color,
                                                    borderRadius: '4px',
                                                    transition: 'width 0.6s ease'
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Language Distribution Breakdown */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                            gap: '24px'
                        }}>
                            {/* By Interface Language */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '28px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0'
                            }}>
                                <h3 style={{
                                    color: '#3c5a5d',
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    margin: '0 0 20px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    🌐 Interface Language Distribution
                                </h3>

                                {stats?.byLanguage && Object.keys(stats.byLanguage).length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {Object.entries(stats.byLanguage).map(([code, count]) => {
                                            const info = getLanguageInfo(code);
                                            const pct = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : 0;
                                            return (
                                                <div key={code} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#1e293b' }}>
                                                            <span style={{ fontSize: '1.2rem' }}>{info.flag}</span>
                                                            <span>{info.name}</span>
                                                            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500' }}>({code})</span>
                                                        </div>
                                                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>
                                                            {count} <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '500' }}>({pct}%)</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: '#3c5a5d', borderRadius: '3px' }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                        No language data available
                                    </div>
                                )}
                            </div>

                            {/* By Learning Language */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '28px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0'
                            }}>
                                <h3 style={{
                                    color: '#3c5a5d',
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    margin: '0 0 20px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    📖 Target Learning Language
                                </h3>

                                {stats?.byLearningLanguage && Object.keys(stats.byLearningLanguage).length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {Object.entries(stats.byLearningLanguage).map(([code, count]) => {
                                            const info = getLanguageInfo(code);
                                            const pct = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : 0;
                                            return (
                                                <div key={code} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#1e293b' }}>
                                                            <span style={{ fontSize: '1.2rem' }}>{info.flag}</span>
                                                            <span>{info.name}</span>
                                                            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500' }}>({code})</span>
                                                        </div>
                                                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>
                                                            {count} <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '500' }}>({pct}%)</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: '#10b981', borderRadius: '3px' }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                        No target learning language data available
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
